import { Activity } from "lucide-react";
import { useMemo, useState } from "react";
import { usePageHeader } from "../../components/shell/PageHeaderContext";
import { Spinner } from "../../components/Spinner";
import { formatTimestamp } from "../../lib/format";
import { useAdminAnalytics } from "../../lib/queries";
import { EmptyRow, PageError, StatTile } from "./shared";

const PERIODS = [{ days: 7, label: "7 días" }, { days: 30, label: "30 días" }, { days: 90, label: "90 días" }];

/** Spanish labels for the event vocabulary of the user stories; the raw name stays visible below. */
const EVENT_LABELS: Record<string, string> = {
  trip_reserve_clicked: "Clic en Reservar", reservation_modal_opened: "Modal de reservación abierto", reservation_request_submitted: "Solicitud de reservación enviada",
  reservation_request_failed: "Solicitud de reservación fallida", apartado_offer_viewed: "Oferta de Apartado vista", apartado_offer_selected: "Oferta de Apartado aceptada",
  planning_route_selected: "Ruta de planeación elegida", wallet_goal_saved: "Meta de Wallet Libre guardada", apartado_transfer_started: "Transferencia a Apartado iniciada",
  apartado_transfer_completed: "Transferencia a Apartado completada", apartado_transfer_failed: "Transferencia a Apartado fallida", reservation_history_viewed: "Mis solicitudes vista",
  reservation_history_searched: "Búsqueda en Mis solicitudes", reservation_history_request_opened: "Solicitud abierta desde el historial", apartado_started_from_history: "Apartado iniciado desde el historial",
  apartado_goal_change_started: "Ajuste de meta iniciado", apartado_goal_changed: "Meta de Apartado cambiada", apartado_fund_disposal_started: "Disposición de fondos iniciada",
  apartado_fund_disposal_completed: "Disposición de fondos completada", personalized_trip_card_clicked: "Tarjeta «Viaje a la medida» pulsada", personalized_trip_step_completed: "Paso del formulario a la medida completado",
  personalized_trip_request_submitted: "Solicitud a la medida enviada", itinerary_word_uploaded: "Word de itinerario subido", itinerary_extraction_completed: "Extracción de itinerario completada",
  itinerary_product_published: "Viaje publicado", kuspit_allocation_started: "Asignación Kuspit iniciada", kuspit_sale_status_changed: "Orden de venta Kuspit cambió de estado",
  kuspit_purchase_status_changed: "Orden de compra Kuspit cambió de estado", kuspit_allocation_reconciled: "Asignación Kuspit conciliada", kuspit_allocation_recovery_opened: "Recuperación Kuspit abierta",
  trip_saved: "Viaje guardado / quitado", saved_trips_viewed: "Guardados vistos",
  account_registered: "Cuenta creada"
};
const SOURCE_LABELS: Record<string, string> = { web: "App", api: "API", worker: "Worker" };

/** Product analytics: counts per event for the period, the daily curve and the latest events. */
export function AdminAnalyticsPage() {
  usePageHeader("Eventos de producto", "Los eventos de las historias de usuario, sin datos personales: edades, pasaportes y contacto nunca se registran.");
  const [days, setDays] = useState(30);
  const [name, setName] = useState<string | undefined>(undefined);
  const analytics = useAdminAnalytics(days, name);
  const data = analytics.data;
  const total = useMemo(() => (data?.summary ?? []).reduce((sum, row) => sum + row.count, 0), [data]);
  const users = useMemo(() => (data?.summary ?? []).reduce((max, row) => Math.max(max, row.users), 0), [data]);
  const peak = Math.max(1, ...(data?.daily ?? []).map((day) => day.count));
  const silent = (data?.knownEvents ?? []).filter((event) => !data?.summary.some((row) => row.name === event));

  return (
    <div className="page-container">
      <div className="discover-toolbar">
        <div className="chip-row" role="tablist" aria-label="Periodo">
          {PERIODS.map((period) => <button key={period.days} type="button" role="tab" aria-selected={days === period.days} className={`chip${days === period.days ? " active" : ""}`} onClick={() => setDays(period.days)}>{period.label}</button>)}
        </div>
        <select className="standalone-input small" value={name ?? ""} onChange={(event) => setName(event.target.value || undefined)} aria-label="Filtrar por evento">
          <option value="">Todos los eventos</option>
          {(data?.knownEvents ?? []).map((event) => <option key={event} value={event}>{EVENT_LABELS[event] ?? event}</option>)}
        </select>
        <span className="toolbar-count">{total} eventos en {days} días</span>
      </div>
      {analytics.isLoading && <div className="page-state"><Spinner label="Cargando eventos" /></div>}
      {analytics.isError && <PageError title="No pudimos cargar los eventos" retry={() => void analytics.refetch()} />}
      {data && (
        <>
          <div className="stat-grid compact">
            <StatTile label="Eventos" value={String(total)} hint={`últimos ${days} días`} icon={Activity} compact />
            <StatTile label="Tipos distintos" value={String(data.summary.length)} hint={`de ${data.knownEvents.length} definidos`} compact />
            <StatTile label="Usuarios (máx. por evento)" value={String(users)} hint="usuarios distintos en el evento más frecuente" compact />
            <StatTile label="Sin registros" value={String(silent.length)} hint="eventos definidos que no ocurrieron" compact />
          </div>

          <div className="card analytics-chart" aria-label="Eventos por día">
            <span className="rail-title">Eventos por día{name ? ` · ${EVENT_LABELS[name] ?? name}` : ""}</span>
            {data.daily.length ? (
              <div className="bars">
                {data.daily.map((day) => (
                  <div key={day.day} className="bar" style={{ height: `${Math.max(3, Math.round((day.count / peak) * 100))}%` }} title={`${day.day} · ${day.count}`}>
                    <span className="sr-only">{day.day}: {day.count} eventos</span>
                  </div>
                ))}
              </div>
            ) : <p className="rail-empty">Sin eventos en este periodo.</p>}
          </div>

          <div className="table-wrap">
            <table className="admin-table">
              <thead><tr><th>Evento</th><th>Veces</th><th>Usuarios</th><th>Último</th></tr></thead>
              <tbody>
                {data.summary.length === 0 && <EmptyRow colSpan={4}>Todavía no hay eventos registrados en este periodo.</EmptyRow>}
                {data.summary.map((row) => (
                  <tr key={row.name} className={name === row.name ? "row-selected" : undefined}>
                    <td><button type="button" className="link-button" onClick={() => setName(name === row.name ? undefined : row.name)}><div className="table-primary"><strong>{EVENT_LABELS[row.name] ?? row.name}</strong><span className="mono">{row.name}</span></div></button></td>
                    <td translate="no">{row.count}</td>
                    <td translate="no">{row.users}</td>
                    <td>{formatTimestamp(row.lastSeen)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <span className="rail-title section-eyebrow">Últimos eventos</span>
          <div className="table-wrap">
            <table className="admin-table">
              <thead><tr><th>Cuándo</th><th>Evento</th><th>Origen</th><th>Propiedades</th></tr></thead>
              <tbody>
                {data.recent.length === 0 && <EmptyRow colSpan={4}>Sin eventos recientes.</EmptyRow>}
                {data.recent.map((event) => (
                  <tr key={event.id}>
                    <td>{formatTimestamp(event.occurredAt)}</td>
                    <td><div className="table-primary"><strong>{EVENT_LABELS[event.name] ?? event.name}</strong><span className="mono">{event.name}</span></div></td>
                    <td>{SOURCE_LABELS[event.source] ?? event.source}</td>
                    <td><code className="mono small">{Object.entries(event.properties).map(([key, value]) => `${key}=${String(value)}`).join(" · ") || "—"}</code></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
